import type { QueryView } from '@krp/client-runtime'

import type { FreshnessVm } from '../../pricing/model/types'
import type { CommandAttempt, ErrorVm } from '../../pricing/model/types'

/**
 * Модель импорта накладных 1С (срез UB1b). Ответы сервера — в кэше RTK Query;
 * здесь типы ответов и ТОЛЬКО намерение человека (разметка, форма профиля).
 */

export const INVOICE_COLUMN_ROLES = [
  'None',
  'LineNumber',
  'Name',
  'Sku',
  'Barcode',
  'Quantity',
  'Unit',
  'PurchasePrice',
  'Sum',
  'VatRate',
  'VatAmount',
  'Comment',
  'Code',
  'PriceWithVat',
  'Discount',
  'Country',
  'CustomsDeclaration',
  'ExpiryDate',
] as const
export type InvoiceColumnRole = (typeof INVOICE_COLUMN_ROLES)[number]

export const PROFILE_MATCHED_BY = ['None', 'Explicit', 'Fingerprint', 'SupplierHint', 'AutoDetect'] as const
export type ProfileMatchedBy = (typeof PROFILE_MATCHED_BY)[number]

export const ISSUE_SEVERITIES = ['Info', 'Warning', 'Error'] as const
export type IssueSeverity = (typeof ISSUE_SEVERITIES)[number]

// --- ответы сервера ---------------------------------------------------------------------------------------

export interface ProfileColumn {
  role: InvoiceColumnRole
  headerText: string | null
  columnIndex: number | null
  isRequiredOverride: boolean | null
}

export interface ImportProfile {
  id: string
  name: string
  supplierName: string | null
  headerFingerprint: string | null
  sheetNameHint: string | null
  headerRowIndex: number | null
  defaultUnit: string | null
  defaultVatRate: string | null
  priceIncludesVat: boolean
  quantityInPackages: boolean
  isActive: boolean
  createdAt: string
  updatedAt: string
  createdBy: string
  usageCount: number
  lastUsedAt: string | null
  columns: readonly ProfileColumn[]
}

export interface MappingSnapshotColumn {
  columnIndex: number
  headerText: string | null
  role: InvoiceColumnRole
}

export interface MappingSnapshot {
  headerRowIndex: number | null
  matchedBy: ProfileMatchedBy
  profileId: string | null
  profileName: string | null
  columns: readonly MappingSnapshotColumn[]
}

export interface ImportedFile {
  id: string
  fileName: string
  contentType: string
  sizeBytes: number
  sha256: string
  uploadedAt: string
  uploadedBy: string
  sheetName: string | null
  headerRowIndex: number | null
  parserVersion: string
  supplyId: string | null
  importProfileId: string | null
  effectiveMapping: MappingSnapshot | null
}

export interface ValidationIssue {
  rowNumber: number | null
  role: InvoiceColumnRole | null
  columnIndex: number | null
  code: string
  severity: IssueSeverity
  message: string
  rawValue: string | null
  suggestedValue: string | null
  relatedRows: readonly number[] | null
}

export interface ValidationReport {
  fileId: string
  isBlocked: boolean
  errorCount: number
  warningCount: number
  infoCount: number
  rowsTotal: number
  rowsValid: number
  rowsWithErrors: number
  rowsWithWarnings: number
  declaredTotal: number | null
  computedTotal: number
  isTruncated: boolean
  validatedAt: string
  issues: readonly ValidationIssue[]
}

export interface DetectedColumn {
  columnIndex: number
  headerText: string | null
  suggestedRole: InvoiceColumnRole
  score: number | null
  isApplied: boolean
}

export interface ColumnMapping {
  headerRowIndex: number | null
  matchedBy: ProfileMatchedBy
  profileId: string | null
  profileName: string | null
  columns: readonly DetectedColumn[]
  missingRequiredRoles: readonly InvoiceColumnRole[]
}

export interface ParsedInvoiceRow {
  sourceRowNumber: number
  name: string | null
  sku: string | null
  barcode: string | null
  quantity: number | null
  unit: string | null
  purchasePrice: number | null
  sum: number | null
  vatRate: string | null
  vatAmount: number | null
  notes: readonly string[]
}

export interface Worksheet {
  name: string
  position: number
  isVisible: boolean
  rowCount: number
  columnCount: number
}

export interface ParsedInvoice {
  fileId: string
  fileName: string
  sheetName: string
  headerRowIndex: number | null
  mapping: ColumnMapping
  rows: readonly ParsedInvoiceRow[]
  worksheets: readonly Worksheet[]
  declaredTotal: number | null
  notes: readonly string[]
  validation: ValidationReport | null
}

// --- намерение человека (слайс) --------------------------------------------------------------------------

export interface ProfileFilter {
  includeInactive: boolean
  supplier: string
}

export const DEFAULT_PROFILE_FILTER: ProfileFilter = { includeInactive: false, supplier: '' }

export interface UploadParams {
  sheet: string
  headerRow: string
  profileId: string
  supplier: string
}

export const EMPTY_UPLOAD_PARAMS: UploadParams = { sheet: '', headerRow: '', profileId: '', supplier: '' }

export interface ProfileColumnDraft {
  role: InvoiceColumnRole
  headerText: string
  columnIndex: string
}

export interface ProfileDraft {
  /** `null` — новый профиль; иначе правка существующего. */
  id: string | null
  name: string
  supplierName: string
  sheetNameHint: string
  headerRowIndex: string
  defaultUnit: string
  defaultVatRate: string
  priceIncludesVat: boolean
  quantityInPackages: boolean
  columns: ProfileColumnDraft[]
}

export interface MappingDraft {
  /** Роль колонки по её номеру; нет записи — берётся предложение сервера. */
  roles: Record<string, InvoiceColumnRole>
  sheetName: string | null
  headerRow: string | null
  saveAsProfile: boolean
  profileName: string
  supplierName: string
  defaultUnit: string
  defaultVatRate: string
  priceIncludesVat: boolean
  quantityInPackages: boolean
}

export const EMPTY_MAPPING_DRAFT: MappingDraft = {
  roles: {},
  sheetName: null,
  headerRow: null,
  saveAsProfile: false,
  profileName: '',
  supplierName: '',
  defaultUnit: '',
  defaultVatRate: '',
  priceIncludesVat: false,
  quantityInPackages: false,
}

/** Что известно о последней загрузке: ключи переживают повтор после обрыва. */
export interface UploadAttempt {
  fingerprint: string
  attempt: CommandAttempt
}

export interface ImportIntentState {
  profileFilters: Record<string, ProfileFilter>
  uploadParams: Record<string, UploadParams>
  uploadAttempts: Record<string, UploadAttempt>
  profileEditors: Record<string, ProfileDraft | null>
  /** `rid:fileId` → разметка колонок. */
  mappings: Record<string, MappingDraft>
  /** Ключ команды (`kind:rid:entity`) → ключи попытки; снимаются при успехе и смене содержимого. */
  attempts: Record<string, CommandAttempt>
}

// --- vm ---------------------------------------------------------------------------------------------------

export type { CommandAttempt, FreshnessVm, ErrorVm }

export interface ProfileRowVm {
  id: string
  name: string
  supplier: string
  isActive: boolean
  columnsText: string
  usageText: string
  updatedText: string
}

export interface ProfileEditorVm {
  draft: ProfileDraft
  problem: string | null
  canSave: boolean
  busy: boolean
  error: ErrorVm | null
  unknownOutcome: boolean
  roleOptions: readonly { value: InvoiceColumnRole; label: string }[]
}

export interface ProfilesVm {
  resourceId: string
  configurationVersion: string | null
  query: QueryView
  filter: ProfileFilter
  rows: readonly ProfileRowVm[]
  editor: ProfileEditorVm | null
  removal: { busy: boolean; error: ErrorVm | null }
}

export interface ProfilesActions {
  reload: () => void
  filterChanged: (filter: Partial<ProfileFilter>) => void
  createStarted: () => void
  editStarted: (profileId: string) => void
  editorChanged: (patch: Partial<Omit<ProfileDraft, 'columns' | 'id'>>) => void
  editorColumnChanged: (index: number, patch: Partial<ProfileColumnDraft>) => void
  editorColumnAdded: () => void
  editorColumnRemoved: (index: number) => void
  editorSaved: () => void
  editorClosed: () => void
  deleted: (profileId: string) => void
}

export interface MappingColumnVm {
  columnIndex: number
  columnText: string
  headerText: string
  role: InvoiceColumnRole
  suggested: InvoiceColumnRole
  /** Неприменённое предложение сервера: подсказка, а не назначенная роль. */
  suggestionText: string | null
}

export interface MappingEditorVm {
  columns: readonly MappingColumnVm[]
  roleOptions: readonly { value: InvoiceColumnRole; label: string }[]
  draft: MappingDraft
  sheetName: string
  headerRow: string
  worksheets: readonly string[]
  missingRequired: readonly string[]
  problem: string | null
  canApply: boolean
  busy: boolean
  error: ErrorVm | null
  unknownOutcome: boolean
}

export interface PreviewRowVm {
  rowNumber: number
  name: string
  sku: string
  barcode: string
  quantityText: string
  priceText: string
  sumText: string
  notes: string
}

export interface ReportVm {
  query: QueryView
  isBlocked: boolean
  summary: readonly string[]
  totalsText: string | null
  validatedAtText: string | null
  truncated: boolean
  issues: readonly { key: string; severity: IssueSeverity; severityLabel: string; where: string; message: string; code: string }[]
}

export interface UploadFormVm {
  params: UploadParams
  busy: boolean
  error: ErrorVm | null
  unknownOutcome: boolean
  profileOptions: readonly { value: string; label: string }[]
}

export interface WizardVm {
  resourceId: string
  configurationVersion: string | null
  upload: UploadFormVm
  /** Разобранная накладная после загрузки/разметки в этом окне; `null` — файл ещё не загружали. */
  parsed: {
    fileId: string
    fileName: string
    matchedByText: string
    profileText: string | null
    notes: readonly string[]
    preview: readonly PreviewRowVm[]
    previewTruncated: boolean
    editor: MappingEditorVm
    report: ReportVm | null
  } | null
}

export interface WizardActions {
  paramsChanged: (patch: Partial<UploadParams>) => void
  upload: (file: File) => void
  roleChanged: (columnIndex: number, role: InvoiceColumnRole) => void
  mappingChanged: (patch: Partial<Omit<MappingDraft, 'roles'>>) => void
  applyMapping: () => void
  openFile: () => void
  openProfiles: () => void
  reload: () => void
}

export interface FileVm {
  resourceId: string
  fileId: string
  configurationVersion: string | null
  file: {
    query: QueryView
    freshness: null
    info: readonly { label: string; value: string }[]
    mapping: readonly { column: string; header: string; role: string }[]
    matchedByText: string | null
  }
  report: ReportVm
  validation: { busy: boolean; error: ErrorVm | null; unknownOutcome: boolean }
  /** Отказ последнего скачивания — текст сервера дословно; ставит binding, файл в Redux не попадает. */
  download: { errorText: string | null }
}

export interface FileActions {
  reload: () => void
  validate: () => void
  downloadOriginal: () => void
  downloadReportCsv: () => void
  /** Окно «Поставка из накладной» с этим файлом — идентификатор не переносится руками (B-2 приёмки U9). */
  createSupply: () => void
  close: () => void
}
